import { z } from 'zod';
import { PAYMENT_METHOD_LABELS } from '../invoices-out/payments.js';
/**
 * Gutscheine (Modul `vouchers`): Alle Beträge eines Gutscheins sind Bruttobeträge: der Betrag, den der Käufer zahlt (Entscheidung von Artur, 30.10.2026, nach kurzem Versuch mit Nettobeträgen). Ein Gutschein ist ein persönliches Wertguthaben, das der Betrieb ausstellt. Er hat einen eigenen, nicht erratbaren Code und einen Betrag, der sich mit
 * jeder Einlösung mindert. Wirkia speichert nur den Hash des Codes: Wer die Datenbank liest, kann daraus keinen Gutschein einlösen, und der Code lässt sich nach dem Ausstellen nicht
 * mehr anzeigen. Davon zu trennen sind die Rabattcodes (fester Code, den jeder Fahrgast einsetzen kann), siehe passenger.ts.
 */
/** Zeichen des Codes: keine 0, O, 1, I, L, Q und U, die sich auf Papier oder am Telefon verwechseln lassen */
export const GIFT_CODE_ALPHABET = 'ABCDEFGHJKMNPRSTVWXYZ23456789';
/** Primzahl und zugleich Länge des Alphabets: So erkennt die Prüfstelle jede Änderung eines einzelnen Zeichens */
const CHECK_MODULUS = GIFT_CODE_ALPHABET.length;
const checkChar = (body) => {
    let sum = 0;
    for (let i = 0; i < body.length; i++)
        sum += (GIFT_CODE_ALPHABET.indexOf(body[i]) + 1) * (i + 1);
    return GIFT_CODE_ALPHABET[sum % CHECK_MODULUS];
};
/** Code ohne Trennstriche und Leerzeichen, in Großbuchstaben. Die verwechselbaren Zeichen 0, O, 1, I, L und U kommen im Alphabet nicht vor, ein Code damit ist also falsch. */
export const normalizeGiftCode = (input) => input.toUpperCase().replace(/[^A-Z0-9]/g, '');
/** Hängt die Prüfstelle an 15 Zeichen an und ergibt den Code mit 16 Zeichen */
export function buildGiftCode(body) {
    return `${body}${checkChar(body)}`;
}
/** Stimmen Länge, Zeichen und Prüfstelle? So fällt ein Tippfehler auf, bevor etwas gesucht wird. */
export function isValidGiftCode(raw) {
    const code = normalizeGiftCode(raw);
    if (code.length !== 16 || ![...code].every((c) => GIFT_CODE_ALPHABET.includes(c)))
        return false;
    return checkChar(code.slice(0, 15)) === code[15];
}
/** XXXX-XXXX-XXXX-XXXX */
export const formatGiftCode = (code) => (normalizeGiftCode(code).match(/.{1,4}/g) ?? []).join('-');
export const GIFT_STATUSES = ['active', 'redeemed', 'expired', 'void'];
export const GIFT_STATUS_LABELS = { active: 'Gültig', redeemed: 'Eingelöst', expired: 'Abgelaufen', void: 'Gesperrt' };
export const GIFT_ENTRY_KINDS = ['issue', 'redeem', 'void'];
export const GIFT_ENTRY_LABELS = { issue: 'Ausgestellt', redeem: 'Eingelöst', void: 'Gesperrt, Restbetrag verfallen' };
/** Höchstbetrag eines Gutscheins (5.000 €). Darüber prüft Wirkia nicht weiter, das Geldwäschegesetz verlangt dort mehr Sorgfalt. */
export const GIFT_MAX_CENTS = 5_000_00;
const day = z.iso.date('Bitte ein Datum angeben');
/** Wie ein verkaufter Gutschein bezahlt wurde. Eine Zahlung per Gutschein oder Verrechnung gehört nicht dazu. */
export const GIFT_SALE_METHODS = ['bank_transfer', 'sepa_debit', 'cash', 'card', 'paypal'];
export const GIFT_SALE_METHOD_LABELS = Object.fromEntries(GIFT_SALE_METHODS.map((m) => [m, PAYMENT_METHOD_LABELS[m]]));
export const GIFT_ORIGINS = ['sold', 'free'];
const text = (max) => z.string().trim().max(max).nullable().optional().transform((v) => (v === undefined ? undefined : v ? v : null));
export const issueGiftInput = z
    .object({
    amountCents: z.number('Bitte einen Betrag angeben').int('Betrag in Cent').min(100, 'Mindestens 1 €').max(GIFT_MAX_CENTS, 'Höchstens 5.000 € je Gutschein'),
    /** Letzter Tag, an dem der Gutschein gilt. Leer: Der Gutschein verfällt nach drei Jahren (Regelverjährung, § 195 BGB). */
    expiresOn: day.nullable().optional(),
    recipientName: text(120),
    buyerContactId: z.uuid().nullable().optional(),
    buyerName: text(120),
    /** Widmung auf dem Gutschein, zum Beispiel „Alles Gute zum Geburtstag“ */
    message: text(300),
    note: text(1000),
    /**
     * Verkauf: der Gutschein-Artikel (Produkt mit einer Buchungsgruppe der Art „Gutschein“), wie und wann der Käufer bezahlt hat. Daraus kennt Wirkia das Sachkonto der Verbindlichkeit.
     * Ohne Angabe ist der Gutschein verschenkt: Es gibt keinen Zahlungseingang, der Aufwand entsteht erst bei der Einlösung.
     */
    sale: z.object({ productId: z.uuid('Bitte den Gutschein-Artikel wählen'), method: z.enum(GIFT_SALE_METHODS, 'Bitte die Zahlungsart wählen'), paidOn: day }).strict().nullable().optional(),
})
    .strict();
export const giftCodeInput = z.object({ code: z.string().trim().min(8, 'Bitte den Code eingeben').max(40, 'Der Code ist zu lang') }).strict();
export const redeemGiftInput = z
    .object({
    code: z.string().trim().min(8, 'Bitte den Code eingeben').max(40),
    amountCents: z.number('Bitte einen Betrag angeben').int('Betrag in Cent').min(1, 'Der Betrag muss größer als 0 sein').max(GIFT_MAX_CENTS),
    reference: text(200),
    /** Ausgestellte Rechnung, die der Gutschein bezahlt (Zahlungsart „Gutschein“). Dafür braucht der Benutzer das Recht, Zahlungen zu erfassen. */
    invoiceId: z.uuid().nullable().optional(),
})
    .strict();
export const voidGiftInput = z.object({ reason: z.string().trim().min(5, 'Bitte einen Grund angeben (mindestens 5 Zeichen)').max(500) }).strict();
export const patchGiftInput = z
    .object({ expiresOn: day.optional(), note: text(1000) })
    .strict()
    .refine((v) => Object.values(v).some((x) => x !== undefined), 'Keine Änderung angegeben');
export const giftPdfInput = z.object({ code: z.string().trim().min(8).max(40) }).strict();
export const listGiftsQuery = z.object({ status: z.enum(GIFT_STATUSES).optional(), q: z.string().trim().max(100).optional() });
export const giftLiabilitiesQuery = z.object({ from: day, to: day, format: z.enum(['json', 'csv']).default('json') });
//# sourceMappingURL=gift.js.map